import React, { useState, useRef, useEffect } from "react";
import {
  verifySingleSideLocally,
  identifyDocumentFromBlob,
  detectIfDocument,
  startAutoCapture, // Added import
} from "../../utils/localVerificationService";
import "./DocumentCapture.css";
import FormError from "../UI/FormError";

function DocumentCapture({
  documentType,
  onBack,
  onContinue,
  side = "front",
  isSubmitting = false,
}) {
  const [captureMode, setCaptureMode] = useState("guide"); // guide, camera, review
  const [capturedImage, setCapturedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [detectionState, setDetectionState] = useState({
    detected: false,
    progress: 0,
    stable: false,
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const stopCaptureRef = useRef(null); // Ref to hold the cleanup function

  const needsBackSide = documentType !== "passport";
  const currentSide = side;

  useEffect(() => {
    if (captureMode === "camera") {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [captureMode]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: 1280, height: 720 },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        // Initialize auto-capture loop when video metadata loads
        videoRef.current.onloadedmetadata = async () => {
          stopCaptureRef.current = await startAutoCapture(
            videoRef.current,
            (blob) => handleAutoCapture(blob),
            { side: currentSide, interval: 1000 },
          );
        };
      }
    } catch (error) {
      console.error("Camera access error:", error);
      alert("Unable to access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    // Stop the auto-capture loop
    if (stopCaptureRef.current) {
      stopCaptureRef.current();
      stopCaptureRef.current = null;
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
  };

  const handleAutoCapture = async (blob) => {
    setCapturedImage(blob);
    setCaptureMode("review");
    stopCamera(); // Clean up camera and loop immediately

    // Verify the auto-captured side
    if (currentSide === "front") {
      setDisabled(true);
      try {
        const result = await detectIfDocument(blob, side);
        if (result?.isDocument) {
          setError(null);
          setDisabled(false);
        } else {
          setError("Auto-capture failed validation. Please retake.");
          setDisabled(true);
        }
      } catch (e) {
        console.error("Verification error:", e);
        setDisabled(true);
      }
    }
  };

  const capturePhoto = async () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(
        async (blob) => {
          handleAutoCapture(blob); // Reuse auto-capture logic for consistency
        },
        "image/jpeg",
        0.95,
      );
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setCapturedImage(file);
      setCaptureMode("review");
      if (currentSide === "front") {
        setDisabled(true);
        try {
          const result = await detectIfDocument(file, side);
          if (result?.isDocument) {
            setError(null);
            setDisabled(false);
          } else {
            setError("Provided document is not valid!");
            setDisabled(true);
          }
        } catch (e) {
          console.error("Verification error:", e);
          setDisabled(true);
        }
      }
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setError(null);
    setDisabled(false);
    setCaptureMode("camera");
  };

  const handleConfirm = () => {
    onContinue(capturedImage, currentSide);
  };

  const getDocumentName = () => {
    const names = {
      "national-id": "National ID",
      passport: "Passport",
      "drivers-license": "Driver's License",
    };
    return names[documentType] || "Document";
  };

  /* Previous version (Single return with conditional rendering) 
  return (
    <div className="capture-container">
      {captureMode === "guide" && (
        <div className="capture-card">
          <div className="capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "75%" }}
              ></div>
            </div>
          </div>

          <div>
            <h2 className="capture-title">
              Take photo of {currentSide === "front" ? "front" : "back"} side
            </h2>
            <p className="capture-subtitle">
              {getDocumentName()} - {currentSide === "front" ? "Front" : "Back"}
            </p>

            <div className="guide-section">
              <div className="document-frame">
                <svg viewBox="0 0 200 130" className="frame-illustration">
                  <rect
                    x="10"
                    y="10"
                    width="180"
                    height="110"
                    rx="8"
                    fill="#f0f5ff"
                    stroke="#121212"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  <text
                    x="100"
                    y="70"
                    textAnchor="middle"
                    fill="#121212"
                    fontSize="14"
                    fontWeight="600"
                  >
                    {currentSide === "front" ? "FRONT" : "BACK"}
                  </text>
                </svg>
              </div>

              <div className="guidelines-list">
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Place document on a flat surface</span>
                </div>
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Ensure all corners are visible</span>
                </div>
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Avoid glare and shadows</span>
                </div>
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Text should be clear and readable</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="action-buttons">
              <button
                className="action-btn capture-btn"
                onClick={() => setCaptureMode("camera")}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="13" r="4" strokeWidth="2" />
                </svg>
                Take Photo
              </button>
              <button
                className="action-btn upload-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="17 8 12 3 7 8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="12"
                    y1="3"
                    x2="12"
                    y2="15"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Upload File
              </button>
            </div>
            <div className="capture-footer">Secured by VerifiLite</div>
          </div>
        </div>
      )}

      {captureMode === "camera" && (
        <div className="capture-card camera-mode">
          <div className="camera-view">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="camera-video"
            ></video>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
            <canvas
              ref={overlayCanvasRef}
              className="detection-overlay"
            ></canvas>

            <div className="camera-overlay">
              <div className="document-outline"></div>
              <div className="camera-hint">
                <span>
                  Position {currentSide} of {getDocumentName()} within frame
                </span>
              </div>
            </div>

            <div className="camera-controls">
              <button
                className="control-btn cancel"
                onClick={() => setCaptureMode("guide")}
              >
                Cancel
              </button>
              <button className="control-btn capture" onClick={capturePhoto}>
                <div className="capture-circle"></div>
              </button>
              <button
                className="control-btn upload"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {captureMode === "review" && (
        <div className="capture-card">
          <div className="capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "75%" }}
              ></div>
            </div>
          </div>

          <h2 className="capture-title">Review your photo</h2>
          <p className="capture-subtitle">
            Is the {currentSide} of your {getDocumentName()} easy to read?
          </p>

          <div className="review-section">
            <p className="review-label">UPLOADED DOCUMENT</p>
            <div className="image-preview">
              {capturedImage && (
                <img
                  src={URL.createObjectURL(capturedImage)}
                  alt="Document preview"
                  className="preview-image"
                />
              )}
            </div>
          </div>

          <FormError message={error} className="review-error" />

          <div className="review-buttons">
            <button
              className="review-btn retake"
              onClick={handleRetake}
              disabled={isSubmitting}
            >
              Retake photo
            </button>
            <button
              className="review-btn confirm"
              onClick={handleConfirm}
              disabled={isSubmitting || disabled}
            >
              {isSubmitting ? "Submitting..." : "Looks good"}
            </button>
          </div>
          <div className="capture-footer">Secured by VerifiLite</div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
    </div>
  ); 
  */

  // Guide Screen
  if (captureMode === "guide") {
    return (
      <div className="capture-container">
        <div className="capture-card">
          <div className="capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "75%" }}
              ></div>
            </div>
          </div>

          <div>
            <h2 className="capture-title">
              Take photo of {currentSide === "front" ? "front" : "back"} side
            </h2>
            <p className="capture-subtitle">
              {getDocumentName()} - {currentSide === "front" ? "Front" : "Back"}
            </p>

            <div className="guide-section">
              <div className="document-frame">
                <svg viewBox="0 0 200 130" className="frame-illustration">
                  <rect
                    x="10"
                    y="10"
                    width="180"
                    height="110"
                    rx="8"
                    fill="#f0f5ff"
                    stroke="#121212"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  <text
                    x="100"
                    y="70"
                    textAnchor="middle"
                    fill="#121212"
                    fontSize="14"
                    fontWeight="600"
                  >
                    {currentSide === "front" ? "FRONT" : "BACK"}
                  </text>
                </svg>
              </div>

              <div className="guidelines-list">
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Place document on a flat surface</span>
                </div>
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Ensure all corners are visible</span>
                </div>
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Avoid glare and shadows</span>
                </div>
                <div className="guideline-item">
                  <svg
                    className="guideline-icon check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Text should be clear and readable</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="action-buttons">
              <button
                className="action-btn capture-btn"
                onClick={() => setCaptureMode("camera")}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="13" r="4" strokeWidth="2" />
                </svg>
                Take Photo
              </button>
              <button
                className="action-btn upload-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="17 8 12 3 7 8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="12"
                    y1="3"
                    x2="12"
                    y2="15"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Upload File
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <div className="capture-footer">Secured by VerifiLite</div>
          </div>
        </div>
      </div>
    );
  }

  // Camera Screen
  if (captureMode === "camera") {
    return (
      <div className="capture-container">
        <div className="capture-card camera-mode">
          <div className="camera-view">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="camera-video"
            ></video>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
            <canvas
              ref={overlayCanvasRef}
              className="detection-overlay"
            ></canvas>

            <div className="camera-overlay">
              <div className="document-outline"></div>
              <div className="camera-hint">
                <span>
                  Position {currentSide} of {getDocumentName()} within frame
                </span>
              </div>
            </div>

            <div className="camera-controls">
              <button
                className="control-btn cancel"
                onClick={() => setCaptureMode("guide")}
              >
                Cancel
              </button>
              <button className="control-btn capture" onClick={capturePhoto}>
                <div className="capture-circle"></div>
              </button>
              <button
                className="control-btn upload"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </button>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
        </div>
      </div>
    );
  }

  // Review Screen
  if (captureMode === "review") {
    return (
      <div className="capture-container">
        <div className="capture-card">
          <div className="capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "75%" }}
              ></div>
            </div>
          </div>

          <h2 className="capture-title">Review your photo</h2>
          <p className="capture-subtitle">
            Is the {currentSide} of your {getDocumentName()} easy to read?
          </p>

          <div className="review-section">
            <p className="review-label">UPLOADED DOCUMENT</p>
            <div className="image-preview">
              {capturedImage && (
                <img
                  src={URL.createObjectURL(capturedImage)}
                  alt="Document preview"
                  className="preview-image"
                />
              )}
            </div>
          </div>

          <FormError message={error} className="review-error" />

          <div className="review-buttons">
            <button
              className="review-btn retake"
              onClick={handleRetake}
              disabled={isSubmitting}
            >
              Retake photo
            </button>
            <button
              className="review-btn confirm"
              onClick={handleConfirm}
              disabled={isSubmitting || disabled}
            >
              {isSubmitting || (disabled && !error) ? (
                <>
                  <span className="btn-spinner"></span>
                  {isSubmitting ? "Submitting..." : "Validating..."}
                </>
              ) : (
                "Looks good"
              )}
            </button>
          </div>
          <div className="capture-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  return null;
}

export default DocumentCapture;
